import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useLocation } from "react-router-dom";
import { Building2, Plane, MapPin, Newspaper, BookOpen, Camera, Compass, CalendarDays, FileText } from "lucide-react";
import api from "../lib/api";
import { setNoindex } from "../lib/seo";
import { Spinner, IataBadge } from "../components/common";
import { airlineLogo, pickAirportBg, CITY_SEED_PHOTOS } from "../lib/assets";
import Weather from "../components/Weather";
import NewsFeed from "../components/NewsFeed";
import DiscussionBoard from "../components/DiscussionBoard";
import GuideSections from "../components/GuideSections";
import PhotoGallery from "../components/PhotoGallery";
import Breadcrumbs from "../components/Breadcrumbs";
import { EmbassiesInCity } from "../components/VisaWidgets";
import GeoEvents from "../components/GeoEvents";
import { geoSlug } from "./ExploreGuide";

// Google-palette active colors — a different color per tab on this board
const TAB_COLORS = {
  guide: "#4285F4", airports: "#34A853", airlines: "#EA4335", articles: "#4285F4",
  news: "#7C3AED", photos: "#0D9488", events: "#EA4335",
};

const TABS = [
  { id: "guide", label: "Guide", icon: BookOpen },
  { id: "airports", label: "Airports", icon: Building2 },
  { id: "airlines", label: "Airlines", icon: Plane },
  { id: "articles", label: "Articles", icon: FileText },
  { id: "news", label: "News", icon: Newspaper },
  { id: "photos", label: "Photos", icon: Camera },
  { id: "events", label: "Events", icon: CalendarDays },
];

function TabBar({ tab, setTab, tabs, testid }) {
  return (
    <div className="flex flex-wrap gap-1 border-b border-zinc-200" data-testid={testid}>
      {tabs.map((t) => (
        <button key={t.id} onClick={() => setTab(t.id)} data-testid={`city-tab-${t.id}`}
          style={tab === t.id ? { borderColor: TAB_COLORS[t.id], color: TAB_COLORS[t.id] } : undefined}
          className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-600 border-b-2 -mb-px transition-colors ${tab === t.id ? "" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          <t.icon className="w-4 h-4" /> {t.label}
        </button>
      ))}
    </div>
  );
}

export default function City() {
  const { name } = useParams();
  const [params] = useSearchParams();
  const { hash } = useLocation();
  const country = params.get("country") || "";
  const region = params.get("region") || "";
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  const [guide, setGuide] = useState(null);
  const [guideLoading, setGuideLoading] = useState(true);
  const [tab, setTab] = useState(() => {
    const h = (hash || "").replace("#", "");
    return TABS.some((t) => t.id === h) ? h : "guide";
  });
  const [newsScope, setNewsScope] = useState("city");
  const [newsType, setNewsType] = useState("general");
  const [stateName, setStateName] = useState(null);

  useEffect(() => {
    setC(null); setErr(false); setGuide(null); setGuideLoading(true); setStateName(null);
    api.get(`/cities/${encodeURIComponent(name)}`, { params: { country, region } })
      .then((r) => { setC(r.data); setStateName(r.data.state_name || null); }).catch(() => { setErr(true); setGuideLoading(false); });
  }, [name, country, region]);

  useEffect(() => {
    if (!c) return;
    let alive = true;
    setGuideLoading(true);
    const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    const run = async () => {
      // 1) When we know the state (e.g. Portland, Oregon vs Maine), fetch the exact
      //    slug — SocialPulse's lookup ignores state and mis-resolves duplicate names.
      if (c.state_name && c.country) {
        const direct = `${slugify(c.country)}/${slugify(c.state_name)}/${slugify(c.name)}`;
        try {
          const r = await api.get(`/guide/location/${direct}`);
          if (r.data && r.data.sections && Object.keys(r.data.sections).length) return r.data;
        } catch { /* fall through to resolve */ }
      }
      // 2) Fall back to lookup for single-match cities.
      const rr = await api.get("/guide/resolve", { params: { city: c.name, country: c.country || country, state: c.state_name || "" } });
      const cityNode = rr.data?.city;
      const slug = typeof cityNode === "string" ? cityNode : cityNode?.slug;
      const resolvedState = (cityNode && cityNode.state) || null;
      // Reject a resolved guide whose state doesn't match this city (wrong duplicate).
      if (c.state_name && resolvedState && resolvedState.toLowerCase() !== c.state_name.toLowerCase()) return null;
      if (!slug) return null;
      const rl = await api.get(`/guide/location/${slug}`);
      return rl.data;
    };
    run().then((g) => { if (alive) setGuide(g); })
      .catch(() => { if (alive) setGuide(null); })
      .finally(() => { if (alive) setGuideLoading(false); });
    return () => { alive = false; };
  }, [c, country]);

  // SEO: a city page without a published guide is thin auto-generated content —
  // keep it out of search indexes (Feb-2026 site review).
  useEffect(() => {
    const hasGuideContent = guide && guide.sections && Object.keys(guide.sections).length > 0;
    setNoindex(err || (!guideLoading && !hasGuideContent));
    return () => setNoindex(false);
  }, [guide, guideLoading, err]);

  if (err) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-muted-foreground">City not found.</div>;
  if (!c) return <Spinner full />;

  const HDR = pickAirportBg(c.name);
  const exploreSlug = [c.country, c.state_name, c.name].filter(Boolean).map(geoSlug).join("/");
  const hasGuide = guide && guide.sections && Object.keys(guide.sections).length > 0;
  const tabs = TABS.filter((t) => t.id !== "guide" || guideLoading || hasGuide);
  const activeTab = tabs.some((t) => t.id === tab) ? tab : "airports";

  return (
    <div data-testid="city-detail">
      <section className="relative border-b border-zinc-200">
        <div className="absolute inset-0"><img src={HDR} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-emerald-900/75" /></div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <Breadcrumbs className="text-white mb-3" items={[
            { label: "Countries", to: "/countries" },
            { label: c.country, to: c.iso_country ? `/countries/${c.iso_country}` : undefined },
            ...(c.iso_region ? [{ label: stateName || c.iso_region.split("-").slice(1).join("-"), to: `/regions/${c.iso_region}` }] : []),
            { label: c.name },
          ]} />
          <div className="text-xs font-mono uppercase tracking-widest text-blue-300 mb-2">City Guide</div>
          <Link to={`/cities/${encodeURIComponent(c.name)}?${new URLSearchParams({ ...(country ? { country } : {}), ...(region ? { region } : {}) }).toString()}`} data-testid="city-name-link"
            className="font-heading font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white hover:text-blue-300 transition-colors">{c.name}</Link>
          <div className="flex items-center gap-1.5 text-zinc-200 mt-2 text-lg font-500"><MapPin className="w-5 h-5" />{c.country}</div>
          <div className="mt-5"><Weather lat={c.latitude} lon={c.longitude} label={c.name} /></div>
          <Link to={`/explore/${exploreSlug}`} data-testid="explore-geo-link"
            className="mt-5 inline-flex items-center gap-2 font-heading font-900 text-2xl sm:text-3xl text-[#FBBC05] hover:text-yellow-300 transition-colors">
            <Compass className="w-7 h-7" /> Explore {c.name}
          </Link>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        {/* Top tabs */}
        <div className="pt-6"><TabBar tab={activeTab} setTab={setTab} tabs={tabs} testid="city-tabs-top" /></div>

        <div className="py-8 min-h-[300px]">
          {/* GUIDE */}
          {activeTab === "guide" && (
            <div data-testid="city-guide-panel">
              <h2 className="font-heading font-900 text-blue-600 text-2xl mb-5 flex items-center gap-2"><BookOpen className="w-6 h-6" /> About {c.name}</h2>
              {guideLoading ? <Spinner /> : hasGuide ? <GuideSections guide={guide} /> : <p className="text-sm text-muted-foreground">No detailed guide available for {c.name} yet.</p>}
            </div>
          )}

          {/* AIRPORTS */}
          {activeTab === "airports" && (
            <div data-testid="city-airports-panel">
              <h2 className="font-heading font-900 text-blue-600 text-2xl mb-5 flex items-center gap-2"><Building2 className="w-6 h-6" /> Airports in {c.name}</h2>
              {c.airports.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {c.airports.map((a) => (
                    <Link key={a.id} to={`/airports/${a.iata_code || a.id}`} data-testid={`city-airport-${a.id}`}
                      className="group border border-zinc-300 bg-white overflow-hidden hover:-translate-y-px hover:border-primary transition-[transform,border-color]">
                      <IataBadge code={a.iata_code || a.icao_code || "—"} name={a.name} className="h-28" />
                      <div className="p-4 text-sm text-muted-foreground capitalize">{(a.type || "").replace("_", " ")}</div>
                    </Link>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">No airports listed.</p>}
            </div>
          )}

          {/* AIRLINES */}
          {activeTab === "airlines" && (
            <div data-testid="city-airlines-panel">
              <h2 className="font-heading font-900 text-rose-600 text-2xl mb-5 flex items-center gap-2"><Plane className="w-6 h-6 -rotate-45" /> Airlines flying from {c.name}</h2>
              {c.airlines?.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {c.airlines.map((a) => (
                    <Link key={a.id} to={`/airlines/${a.id}`} data-testid={`city-airline-${a.id}`}
                      className="group border border-zinc-300 bg-white overflow-hidden hover:-translate-y-px hover:border-primary transition-[transform,border-color]">
                      <IataBadge code={a.iata || "—"} name={a.name} logo={airlineLogo(a.iata)} className="h-28" />
                      <div className="p-4 text-sm text-muted-foreground flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{a.country || "—"}</div>
                    </Link>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">No airlines listed for this city.</p>}
            </div>
          )}

          {/* ARTICLES — main destination guide + related reading */}
          {activeTab === "articles" && (
            <div data-testid="city-articles-panel">
              <h2 className="font-heading font-900 text-2xl mb-5 flex items-center gap-2" style={{ color: TAB_COLORS.articles }}>
                <FileText className="w-6 h-6" /> {c.name} articles
              </h2>
              <Link to={`/explore/${exploreSlug}`} data-testid="main-article-card"
                className="block border-2 bg-white p-6 hover:-translate-y-px transition-transform max-w-2xl"
                style={{ borderColor: TAB_COLORS.articles }}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Main article</div>
                <div className="font-heading font-900 text-2xl flex items-center gap-2">
                  <Compass className="w-6 h-6" style={{ color: TAB_COLORS.articles }} /> {c.name} Travel Guide
                </div>
                <p className="text-sm text-zinc-600 mt-2">
                  The complete visitor guide — getting there, getting around, where to stay, things to do,
                  food, safety, local etiquette and traveler FAQ. Generated and kept fresh by TravelerPulse.
                </p>
                <span className="inline-block mt-3 text-sm font-700" style={{ color: TAB_COLORS.articles }}>Read the full guide →</span>
              </Link>
              <div className="mt-6">
                <Link to="/articles" className="text-sm font-700 text-zinc-600 hover:text-primary underline">Browse all TravelerPulse articles →</Link>
              </div>
            </div>
          )}

          {/* EVENTS */}
          {activeTab === "events" && (
            <div data-testid="city-events-panel">
              <h2 className="font-heading font-900 text-2xl mb-5 flex items-center gap-2" style={{ color: TAB_COLORS.events }}>
                <CalendarDays className="w-6 h-6" /> Events in {c.name}
              </h2>
              <GeoEvents slug={exploreSlug} placeName={c.name} />
            </div>
          )}

          {/* NEWS */}
          {activeTab === "news" && (
            <div data-testid="city-news-panel">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <h2 className="font-heading font-900 text-violet-600 text-2xl flex items-center gap-2"><Newspaper className="w-6 h-6" /> {newsScope === "city" ? [c.name, c.state_name].filter(Boolean).join(", ") : c.country} {newsType === "general" ? "news" : "travel news"}</h2>
                <div className="flex flex-wrap gap-2">
                  <div className="inline-flex border border-zinc-300 bg-white text-sm" data-testid="news-type-toggle">
                    <button onClick={() => setNewsType("general")} data-testid="news-type-general" className={`px-3 py-1.5 ${newsType === "general" ? "bg-violet-600 text-white" : "text-muted-foreground hover:text-foreground"}`}>General news</button>
                    <button onClick={() => setNewsType("travel")} data-testid="news-type-travel" className={`px-3 py-1.5 ${newsType === "travel" ? "bg-violet-600 text-white" : "text-muted-foreground hover:text-foreground"}`}>Travel news</button>
                  </div>
                  <div className="inline-flex border border-zinc-300 bg-white text-sm" data-testid="news-scope-toggle">
                    <button onClick={() => setNewsScope("city")} data-testid="news-scope-city" className={`px-3 py-1.5 ${newsScope === "city" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>City</button>
                    <button onClick={() => setNewsScope("country")} data-testid="news-scope-country" className={`px-3 py-1.5 ${newsScope === "country" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>Country</button>
                  </div>
                </div>
              </div>
              {/* Always pad the city query with its state/country so duplicate city
                  names (Portland OR vs Portland ME) never mix news. */}
              <NewsFeed key={`${newsScope}-${newsType}`} path={`/news?category=${newsType === "general" ? "general" : "airport"}&q=${encodeURIComponent(newsScope === "city" ? `${c.name} ${c.state_name || c.country || ""}`.trim() : c.country)}&limit=8&archive_as=${newsScope === "city" ? "city" : "country"}`} />
            </div>
          )}

          {/* PHOTOS */}
          {activeTab === "photos" && (
            <div data-testid="city-photos-panel">
              <h2 className="font-heading font-900 text-emerald-600 text-2xl mb-5 flex items-center gap-2"><Camera className="w-6 h-6" /> {c.name} photos</h2>
              <PhotoGallery entityType="city" entityId={c.state_name ? `${c.name} (${c.state_name})` : c.name} seedPhotos={CITY_SEED_PHOTOS} />
            </div>
          )}
        </div>

        {/* Bottom tabs */}
        <TabBar tab={activeTab} setTab={setTab} tabs={tabs} testid="city-tabs-bottom" />

        {/* Embassies & consulates physically located in this city (§15) */}
        <EmbassiesInCity city={c.name} countryIso={c.iso_country} />

        {/* Discussion board always visible */}
        <div className="py-12">
          <DiscussionBoard entityType="city" entityId={c.state_name ? `${c.name} (${c.state_name})` : c.name} title={`${[c.name, c.state_name].filter(Boolean).join(", ")} discussion board`} />
        </div>
      </div>
    </div>
  );
}
